<template>
  <modal @mounted="initCanvas()">
    <div class="xterminate-instance">
      <button @click="$emit('closeGame', false)" class="xterminate-instance-close">
        <font-awesome-icon :icon="['fas', 'times']" size="2x" />
      </button>
      <div class="xterminate-instance-stamina" :style="[{ width: `${game.life}%` }]"></div>
      <div class="xterminate-instance-data">
        <b>
          {{ $t("portfolio.xterminate.instance.villainLabel") }}
          {{ $t("portfolio.xterminate.instance.weaponLabel") }}<br />
          {{ game.villains }} {{ game.weapons }}
        </b>
      </div>
      <div class="xterminate-instance-status">
        <b>{{ $t("portfolio.xterminate.instance.staminaLabel") }}</b>
      </div>
      <template v-if="!!game.status">
        <div class="xterminate-instance-backdrop"></div>
        <div class="xterminate-instance-final-message">
          <b>{{
            $t(`portfolio.xterminate.instance.${game.status === 1 ? "lose" : "win"}Label`)
          }}</b>
        </div>
        <button @click="resetGame" class="xterminate-instance-restart">
          <b>{{ $t("portfolio.xterminate.instance.buttonLabel") }}</b>
        </button>
      </template>
      <div ref="VirtualContainer" class="xterminate-instance-3d"></div>
    </div>
  </modal>
</template>

<script>
import * as THREE from "three";
import Maze from "@/models/Xterminate/Maze";
import ResourceTracker from "@/models/ResourceTracker";

const Modal = () => import(/* webpackChunkName: "Modal" */ "@/components/Global/Modal.vue");

export default {
  name: "XterminateInstance",
  components: {
    Modal
  },
  data() {
    return {
      resourceTracker: new ResourceTracker(),
      camera: null,
      scene: null,
      renderer: null,
      frameId: null,
      game: {
        life: 50, // 50 means all life
        status: 0, // 0 game continues, 1 game lost, 2 game won
        villains: 0,
        weapons: 0
      }
    };
  },
  methods: {
    /*
     * @function - initCanvas
     * This function initialize scene, camera, and renderer for xterminate instance
     * @param - none
     */
    initCanvas() {
      const { offsetWidth, offsetHeight } = this.$refs.VirtualContainer;

      // Camera
      this.camera = this.resourceTracker.track(
        new THREE.PerspectiveCamera(75, offsetWidth / offsetHeight, 0.1, 1000)
      );

      // Scene
      this.scene = this.resourceTracker.track(Maze(this.camera, this.$bus));

      // Renderer
      this.renderer = this.resourceTracker.track(new THREE.WebGLRenderer({ alpha: true }));
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.renderer.setSize(offsetWidth, offsetHeight);
      this.$refs.VirtualContainer.appendChild(this.renderer.domElement);

      this.animate();
      this.doEvents();
    },
    /*
     * @function - animateEntry
     * Sets scene animation loop with sense and perform methods (agents programming orientation)
     * @param - none
     */
    animate() {
      this.frameId = requestAnimationFrame(this.animate);

      if (this.renderer) {
        this.renderer.render(this.scene, this.camera);

        this.scene.children.forEach(child => {
          if (child.sensing) {
            child.sensing(this.scene);
          }
          if (child.perform) {
            child.perform(this.scene);
          }
        });
      }
    },
    /*
     * @function - doEvents
     * Adds or removes all dom listeners of any 3D model if exists
     * @param - add (Boolean): if TRUE adds event listeners, otherwise removes them
     */
    doEvents(add = true) {
      this.scene.children.forEach(child => {
        if (child.keyboardEvent) {
          window[add ? "addEventListener" : "removeEventListener"]("keydown", child.keyboardEvent);
          window[add ? "addEventListener" : "removeEventListener"]("keyup", child.keyboardEvent);
        }
      });
    },
    /*
     * @function - doResize
     * Redefine 3d scene size when windiw is resized
     * @param - none
     */
    doResize() {
      const { offsetWidth, offsetHeight } = this.$refs.VirtualContainer;

      this.camera.aspect = offsetWidth / offsetHeight;
      this.camera.updateProjectionMatrix();

      this.renderer.setSize(offsetWidth, offsetHeight);
    },
    /*
     * @function - removeCanvas
     * Clears all 3D scene memory dependencies, remove scene dom element and cancel animation loop
     * @param - none
     */
    removeCanvas() {
      this.doEvents(false);
      this.resourceTracker.dispose();
      this.$refs.VirtualContainer.removeChild(this.renderer.domElement);
      cancelAnimationFrame(this.frameId);
      this.frameId = null;
    },
    /*
     * @function - resetGame
     * Clears all 3D scene, reset game data to initial state and recreate 3D canvas
     * @param - none
     */
    resetGame() {
      this.removeCanvas();
      this.damageHero(50);
      this.finishGame(0);
      this.initCanvas();
    },
    /*
     * @function - setGameData
     * Sets current data for game data box
     * @param - villains (Number): Total of active villains
     * @param - weapons (Number): Total of active weapons
     */
    setGameData({ villains, weapons }) {
      this.game.villains = villains;
      this.game.weapons = weapons;
    },
    /*
     * @function - destroyVillain
     * Destroys specific villain
     * @param - villain (threeJS Object Instance): Villain to be destroyed
     */
    destroyVillain(villain) {
      this.resourceTracker.disposeByResource(villain);
    },
    /*
     * @function - damageHero
     * Sets hero stamina
     * @param - life (Number): Current stamina of X-Wayne
     */
    damageHero(life) {
      this.game.life = life;
    },
    /*
     * @function - finishGame
     * Sets game status to lost, won or active
     * @param - status (Number): 0 active, 1 lost, 2 won
     */
    finishGame(status) {
      this.game.status = status;
    }
  },
  created() {
    this.$bus.$on("Xterminate: set game data", this.setGameData);
    this.$bus.$on("Xterminate: villain destroyed", this.destroyVillain);
    this.$bus.$on("Xterminate: hero damaged", this.damageHero);
    this.$bus.$on("Xterminate: game finished", this.finishGame);
  },
  mounted() {
    window.addEventListener("resize", this.doResize);
  },
  beforeDestroy() {
    this.removeCanvas();
    this.$bus.$off("Xterminate: set game data", this.setGameData);
    this.$bus.$off("Xterminate: villain destroyed", this.destroyVillain);
    this.$bus.$off("Xterminate: hero damaged", this.damageHero);
    this.$bus.$off("Xterminate: game finished", this.finishGame);
    window.removeEventListener("resize", this.doResize);
  }
};
</script>

<style lang="scss" scoped>
.xterminate-instance {
  height: 100%;
  position: relative;
  width: 100%;

  &-stamina {
    -webkit-transition: width 0.5s; /* For Safari 3.1 to 6.0 */
    background-color: rgba(0, 0, 255, 0.7);
    background: -webkit-radial-gradient(rgba(0, 255, 255, 0.7), black); /* For Safari 5.1 to 6.0 */
    background: -o-radial-gradient(rgba(0, 255, 255, 0.7), black); /* For Opera 11.1 to 12.0 */
    background: -moz-radial-gradient(rgba(0, 255, 255, 0.7), black); /* For Firefox 3.6 to 15 */
    background: radial-gradient(rgba(0, 255, 255, 0.7), black); /* Standard syntax */
    border-radius: 10px;
    border: 2px solid rgba(0, 255, 255, 0.7);
    box-shadow: 10px 10px 5px rgba(150, 0, 150, 0.7);
    color: green;
    font-family: Papyrus, fantasy;
    height: 5%;
    left: 10%;
    position: absolute;
    text-align: center;
    top: 3%;
    transition: width 0.5s;
    width: 50%;
    z-index: 1;
  }

  &-status {
    -webkit-transition: width 0.5s; /* For Safari 3.1 to 6.0 */
    background-color: rgba(0, 0, 255, 0.7);
    background: -webkit-radial-gradient(rgba(255, 0, 0, 0.7), black); /* For Safari 5.1 to 6.0 */
    background: -o-radial-gradient(rgba(255, 0, 0, 0.7), black); /* For Opera 11.1 to 12.0 */
    background: -moz-radial-gradient(rgba(255, 0, 0, 0.7), black); /* For Firefox 3.6 to 15 */
    background: radial-gradient(rgba(255, 0, 0, 0.7), black); /* Standard syntax */
    border-radius: 10px;
    border: 2px solid rgba(0, 0, 0, 0.7);
    box-shadow: 10px 10px 5px rgba(150, 0, 150, 0.7);
    color: rgba(220, 220, 220, 0.7);
    padding-top: 1%;
    font-family: Papyrus, fantasy;
    font-size: large;
    height: 7%;
    left: 1%;
    position: absolute;
    text-align: center;
    top: 1%;
    transition: width 0.5s;
    width: 10%;
    z-index: 0;
  }

  &-data {
    -webkit-transition: width 0.5s; /* For Safari 3.1 to 6.0 */
    background-color: rgba(0, 0, 255, 0.7);
    background: -webkit-radial-gradient(rgba(255, 0, 0, 0.7), black); /* For Safari 5.1 to 6.0 */
    background: -o-radial-gradient(rgba(255, 0, 0, 0.7), black); /* For Opera 11.1 to 12.0 */
    background: -moz-radial-gradient(rgba(255, 0, 0, 0.7), black); /* For Firefox 3.6 to 15 */
    background: radial-gradient(rgba(255, 0, 0, 0.7), black); /* Standard syntax */
    border-radius: 10px;
    border: 2px solid rgba(0, 0, 0, 0.7);
    box-shadow: 10px 10px 5px rgba(150, 0, 150, 0.7);
    color: rgba(220, 220, 220, 0.7);
    font-family: Papyrus, fantasy;
    font-size: large;
    height: 7%;
    left: 70%;
    padding-top: 1%;
    padding-bottom: 1%;
    position: absolute;
    text-align: center;
    top: 1%;
    word-spacing: 60px;
    width: 20%;
    transition: width 0.5s;
    z-index: 0;
  }

  &-restart {
    -webkit-transition: width 0.5s, height 0.5s, color 0.5s, text-shadow 0.5s, top 0.5s, left 0.5s,
      padding-top 0.5s, font-size 0.5s; /* For Safari 3.1 to 6.0 */
    background-color: rgba(150, 150, 150, 0.7);
    background: -webkit-radial-gradient(
      rgba(150, 150, 150, 0.7),
      black
    ); /* For Safari 5.1 to 6.0 */
    background: -o-radial-gradient(rgba(150, 150, 150, 0.7), black); /* For Opera 11.1 to 12.0 */
    background: -moz-radial-gradient(rgba(150, 150, 150, 0.7), black); /* For Firefox 3.6 to 15 */
    background: radial-gradient(rgba(150, 150, 150, 0.7), black); /* Standard syntax */
    border-radius: 10px;
    border: 2px solid rgba(150, 150, 150, 0.7);
    box-shadow: 10px 10px 5px rgba(0, 0, 0, 0.7);
    color: rgba(200, 0, 0, 1);
    font-family: Century, Gothic;
    font-size: 20px;
    height: 7%;
    left: 45%;
    padding-top: 1%;
    position: absolute;
    text-align: center;
    text-decoration: none;
    text-shadow: 3px 3px rgb(0, 0, 0);
    top: 45%;
    width: 10%;
    transition: width 0.5s, height 0.5s, color 0.5s, text-shadow 0.5s, top 0.5s, left 0.5s,
      padding-top 0.5s, font-size 0.5s;
    z-index: 4;

    :hover {
      color: rgba(0, 0, 0, 1);
      font-size: 30px;
      height: 15%;
      left: 42.5%;
      padding-top: 4%;
      text-shadow: 6px 6px rgb(100, 0, 0);
      top: 39%;
      width: 15%;
    }
  }

  &-final-message {
    -webkit-transition: width 0.5s, height 0.5s, color 0.5s, text-shadow 0.5s, top 0.5s, left 0.5s,
      padding-top 0.5s, font-size 0.5s; /* For Safari 3.1 to 6.0 */
    background-color: rgba(150, 150, 150, 0);
    border-radius: 10px;
    color: rgba(200, 0, 0, 0.7);
    font-family: Papyrus, fantasy;
    font-size: 100px;
    height: 15%;
    left: 15%;
    position: absolute;
    text-align: center;
    text-decoration: none;
    text-shadow: 5px 5px rgb(0, 0, 0);
    top: 15%;
    transition: width 0.5s, height 0.5s, color 0.5s, text-shadow 0.5s, top 0.5s, left 0.5s,
      padding-top 0.5s, font-size 0.5s;
    width: 70%;
    z-index: 4;
  }

  &-backdrop {
    -webkit-transition: width 0.5s, height 0.5s, color 0.5s, text-shadow 0.5s, top 0.5s, left 0.5s,
      padding-top 0.5s, font-size 0.5s; /* For Safari 3.1 to 6.0 */
    background-color: rgba(0, 0, 0, 0.8);
    bottom: 0;
    color: rgba(200, 0, 0, 0.7);
    font-family: Papyrus, fantasy;
    font-size: 100px;
    left: 0;
    right: 0;
    text-align: center;
    text-decoration: none;
    text-shadow: 5px 5px rgb(0, 0, 0);
    top: 0;
    position: absolute;
    transition: width 0.5s, height 0.5s, color 0.5s, text-shadow 0.5s, top 0.5s, left 0.5s,
      padding-top 0.5s, font-size 0.5s;
    z-index: 3;
  }

  &-close {
    background-color: rgba(0, 0, 255, 0.7);
    background: -webkit-radial-gradient(rgba(255, 0, 0, 0.7), black); /* For Safari 5.1 to 6.0 */
    background: -o-radial-gradient(rgba(255, 0, 0, 0.7), black); /* For Opera 11.1 to 12.0 */
    background: -moz-radial-gradient(rgba(255, 0, 0, 0.7), black); /* For Firefox 3.6 to 15 */
    background: radial-gradient(rgba(255, 0, 0, 0.7), black); /* Standard syntax */
    border-radius: 10px;
    border: 2px solid rgba(0, 0, 0, 0.7);
    box-shadow: 10px 10px 5px rgba(150, 0, 150, 0.7);
    color: rgba(220, 220, 220, 0.7);
    margin: 0.25rem 1rem 0.25rem 0.25rem;
    padding: 0.5rem;
    position: absolute;
    right: 0;
    z-index: 4;
  }

  &-3d,
  canvas {
    width: 100%;
    height: 100%;
  }
}
</style>
